import * as React from "react";
import classNames from "classnames";
import styles from "./PercentBreakdown.module.scss";
import type { TransitionStatus } from "react-transition-state";
import { useTransitionMap } from "react-transition-state";
import TooltipWrapper from "../../utils/TooltipWrapper/TooltipWrapper";

export type BreakdownSectionType = {
  weight: number;
  id: string | number;
  colorHex: `#${string}`;
  tooltip?: React.ReactNode;
  nodeLabel?: (isActiveSection: boolean) => React.ReactNode;
};

type PercentBreakdownProps = {
  readonly sections: Array<BreakdownSectionType>;
  readonly UnderflowTooltip?: React.ComponentType<
    React.PropsWithChildren<{ underflow: number }>
  >;
  readonly OverflowTooltip?: React.ComponentType<
    React.PropsWithChildren<{ overflow: number }>
  >;
  readonly total: number;
  readonly percentError?: number;
  readonly dataTestId?: string;
  readonly activeSectionId?: string | number | null;
  readonly collapseSections?: boolean;

  readonly onMouseEnterBreakdownSection?: (sectionId: string | number) => void;
  readonly onMouseLeaveBreakdownSection?: (sectionId: string | number) => void;
  readonly onClickBreakdownSection?: (sectionId: string | number) => void;
  readonly disableBreakdownSectionClick?: (
    sectionId: string | number
  ) => boolean;
};

const calculateLowerBound = (total: number, percentError: number) =>
  total - total * (percentError / 100);
const calculateUpperBound = (total: number, percentError: number) =>
  total + total * (percentError / 100);

export const percentErrorCalculations = {
  calculateLowerBound,
  calculateUpperBound,
};

const getSectionWeightStatus = ({
  isUnderflow,
  isOverflow,
}: {
  isUnderflow: boolean;
  isOverflow: boolean;
}) => {
  if (isUnderflow) return "underflow";
  if (isOverflow) return "overflow";
  return "withinRange";
};

const PercentBreakdown = ({
  sections,
  total,
  UnderflowTooltip,
  OverflowTooltip,
  percentError = 0,
  activeSectionId,
  dataTestId = "percent-breakdown",
  onMouseEnterBreakdownSection,
  onMouseLeaveBreakdownSection,
  onClickBreakdownSection,
  disableBreakdownSectionClick,
  collapseSections,
}: PercentBreakdownProps) => {
  const totalSectionWeight = sections.reduce(
    (acc, entity) => acc + entity.weight,
    0
  );

  const acceptableRange = [
    calculateLowerBound(total, percentError),
    calculateUpperBound(total, percentError),
  ];

  const sectionWeightStatus = getSectionWeightStatus({
    isUnderflow: totalSectionWeight < acceptableRange[0]!,
    isOverflow: totalSectionWeight > acceptableRange[1]!,
  });

  const sectionsDataRef = React.useRef(
    new Map<string | number, BreakdownSectionType>()
  );
  sections.forEach((s) => sectionsDataRef.current.set(s.id, s));

  const { stateMap, toggle, setItem, deleteItem } = useTransitionMap<
    string | number
  >({
    // sections coexist, so multiple items must be able to stay "entered" at once.
    // Without this, toggling one section in auto-exits all the others.
    allowMultiple: true,
    timeout: { enter: 0, exit: 250 },
    onStateChange: ({ key, current }) => {
      if (current.status === "exited") {
        deleteItem(key);
        sectionsDataRef.current.delete(key);
      }
    },
  });

  const prevSectionIdsRef = React.useRef<Set<string | number> | null>(null);

  React.useLayoutEffect(() => {
    const currentIds = new Set(sections.map((s) => s.id));

    if (!prevSectionIdsRef.current) {
      sections.forEach((s) => setItem(s.id, { initialEntered: true }));
    } else {
      for (const id of currentIds) {
        if (!prevSectionIdsRef.current.has(id)) {
          setItem(id);
          toggle(id, true);
        }
      }

      for (const id of prevSectionIdsRef.current) {
        if (!currentIds.has(id)) {
          toggle(id, false);
        }
      }
    }

    prevSectionIdsRef.current = currentIds;
  }, [sections, setItem, toggle]);

  const getWidthState = React.useCallback(
    (status: TransitionStatus, weight: number) => {
      if (status !== "entered") {
        return "0%";
      }
      if (sectionWeightStatus === "overflow") {
        // overflow section width will be equal to the overflowed percentage
        // normalizing the weight of each section by the percent width remaining
        // prettier-ignore
        return `${(weight / totalSectionWeight) * (total / totalSectionWeight) * 100}%`;
      }

      const totalWeight =
        sectionWeightStatus === "withinRange" ? totalSectionWeight : total;

      return `${(weight / totalWeight) * 100}%`;
    },
    [sectionWeightStatus, totalSectionWeight, total]
  );

  if (!total) {
    return (
      <div
        data-testid={dataTestId}
        className="h-full w-full bg-straps-body transition-all"
      />
    );
  }

  const activeSectionIds = new Set(sections.map((s) => s.id));

  const activeItems = sections.map((section, i) => {
    const state = stateMap.get(section.id);
    if (!state || state.status === "exited" || state.status === "unmounted") {
      return null;
    }
    const shouldDisableClick = disableBreakdownSectionClick?.(section.id);
    return (
      <BreakdownSection
        key={section.id}
        collapseSections={collapseSections}
        section={section}
        CSSWidth={getWidthState(state.status, section.weight)}
        isLastSection={
          i === sections.length - 1 && sectionWeightStatus === "withinRange"
        }
        activeSectionId={activeSectionId}
        onClick={shouldDisableClick ? undefined : onClickBreakdownSection}
        onMouseEnter={onMouseEnterBreakdownSection}
        onMouseLeave={onMouseLeaveBreakdownSection}
      />
    );
  });

  const exitingItems = Array.from(stateMap.entries())
    .filter(
      ([key, state]) =>
        !activeSectionIds.has(key) &&
        state.status !== "exited" &&
        state.status !== "unmounted"
    )
    .map(([key, state]) => {
      const section = sectionsDataRef.current.get(key);
      if (!section) return null;
      return (
        <BreakdownSection
          key={key}
          collapseSections={collapseSections}
          section={section}
          CSSWidth={getWidthState(state.status, section.weight)}
          isLastSection={false}
          activeSectionId={activeSectionId}
        />
      );
    });

  return (
    <div data-testid={dataTestId} className="flex h-full w-full transition-all">
      {activeItems}
      {exitingItems}
      {sectionWeightStatus !== "withinRange" && (
        <TooltipWrapper
          fixed
          align="top"
          tooltip={
            sectionWeightStatus === "underflow"
              ? UnderflowTooltip && (
                  <UnderflowTooltip underflow={total - totalSectionWeight} />
                )
              : OverflowTooltip && (
                  <OverflowTooltip overflow={totalSectionWeight - total} />
                )
          }
        >
          <div
            data-testid={`${dataTestId}-margin-of-error-section`}
            className={classNames("h-full flex-grow transition-opacity", {
              [styles.underflow_striped_gradient!]:
                sectionWeightStatus === "underflow",
              [styles.overflow_striped_gradient!]:
                sectionWeightStatus === "overflow",
              "opacity-100":
                activeSectionId === null || activeSectionId === undefined,
              "opacity-25 hover:opacity-100":
                activeSectionId !== null && activeSectionId !== undefined,
            })}
          />
        </TooltipWrapper>
      )}
    </div>
  );
};

export default PercentBreakdown;

const BreakdownSection = ({
  section,
  CSSWidth,
  isLastSection,
  activeSectionId,
  onMouseEnter,
  onMouseLeave,
  onClick,
  collapseSections,
}: {
  readonly section: BreakdownSectionType;
  readonly CSSWidth: string;
  readonly isLastSection: boolean;
  readonly activeSectionId?: string | number | null;
  readonly collapseSections?: boolean;

  readonly onMouseEnter?: (sectionId: string | number) => void;
  readonly onMouseLeave?: (sectionId: string | number) => void;
  readonly onClick?: (sectionId: string | number) => void;
}) => {
  const activeIdNullOrUndefined =
    activeSectionId === null || activeSectionId === undefined;

  const isActiveSection = activeSectionId === section.id;
  return (
    <div
      data-testid="percent-breakdown-section"
      style={{ width: CSSWidth }}
      className={classNames(
        "h-full overflow-hidden bg-transparent transition-all",
        {
          "mr-1": !isLastSection && !collapseSections,
        }
      )}
    >
      <TooltipWrapper fixed align="top" tooltip={section.tooltip}>
        <button
          onMouseEnter={() => onMouseEnter?.(section.id)}
          onMouseLeave={() => onMouseLeave?.(section.id)}
          onClick={() => onClick?.(section.id)}
          style={{ backgroundColor: section.colorHex }}
          className={classNames(
            "h-full w-full min-w-[1px] p-0 transition-all",
            {
              "cursor-pointer": !!onClick,
              "opacity-100": isActiveSection || activeIdNullOrUndefined,
              "opacity-25 hover:opacity-100":
                !activeIdNullOrUndefined && !isActiveSection,
            }
          )}
        >
          {section.nodeLabel?.(isActiveSection) ?? null}
        </button>
      </TooltipWrapper>
    </div>
  );
};
